import { useState } from 'react';
import type { ReactElement } from 'react';

import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Settings2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import './types';

export interface DataTableServerSide {
  rowCount: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  serverSide?: DataTableServerSide;
}

export const DataTable = <TData, TValue>({
  columns,
  data,
  serverSide,
}: DataTableProps<TData, TValue>): ReactElement => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    initialState: { pagination: { pageSize: 20 } },
    state: serverSide
      ? {
          sorting: serverSide.sorting,
          columnFilters: serverSide.columnFilters,
          pagination: serverSide.pagination,
          columnVisibility,
        }
      : { sorting, columnFilters, columnVisibility },
    onSortingChange: serverSide?.onSortingChange ?? setSorting,
    onColumnFiltersChange: serverSide?.onColumnFiltersChange ?? setColumnFilters,
    onPaginationChange: serverSide?.onPaginationChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: !!serverSide,
    manualFiltering: !!serverSide,
    manualSorting: !!serverSide,
    rowCount: serverSide?.rowCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const hideableColumns = table.getAllLeafColumns().filter((c) => c.getCanHide());
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const totalColumnCount = table.getAllLeafColumns().length;
  const showAll = (): void => hideableColumns.forEach((c) => c.toggleVisibility(true));
  const hideAll = (): void => hideableColumns.forEach((c) => c.toggleVisibility(false));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Popover>
          <PopoverTrigger
            type="button"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border-subtle bg-bg-surface px-2.5 text-[0.8rem] font-medium text-text-default outline-none hover:bg-action-ghost-hover focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <Settings2 className="size-3.5" />
            Columnas
            <span className="text-xs text-text-subtle">
              {visibleColumnCount}/{totalColumnCount}
            </span>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 gap-0 overflow-hidden p-0">
            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
              Mostrar columnas
            </p>
            <ScrollArea className="h-64">
              <div className="flex flex-col gap-0.5 px-1 pb-1">
                {hideableColumns.map((c) => {
                  const label = c.columnDef.meta?.title ?? c.id;
                  const inputId = `col-vis-${c.id}`;
                  return (
                    <div
                      key={c.id}
                      className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 hover:bg-action-ghost-hover"
                    >
                      <Checkbox
                        id={inputId}
                        checked={c.getIsVisible()}
                        onCheckedChange={(v) => c.toggleVisibility(!!v)}
                      />
                      <Label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer truncate text-sm font-normal">
                        {label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
            <Separator />
            <div className="flex items-center justify-between gap-1 p-1">
              <Button variant="ghost" size="sm" className="h-7 flex-1 justify-center" onClick={hideAll}>
                Ocultar todas
              </Button>
              <Button variant="ghost" size="sm" className="h-7 flex-1 justify-center" onClick={showAll}>
                Mostrar todas
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border-subtle bg-bg-surface">
        <Table className="min-w-[1400px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumnCount} className="h-24 text-center text-text-subtle">
                  Sin resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-subtle">
          Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount() || 1} ·{' '}
          {serverSide?.rowCount ?? table.getFilteredRowModel().rows.length} resultados
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Siguiente
          </Button>
        </div>
      </div>
    </div>
  );
};
